import * as React from 'react'
import { Control } from 'react-redux-form'

import Icon from 'components/Icon'
import { UI } from 'Icon/ui'

import { Col, Grid, GridGap } from 'components/Grid'

import classNames from 'classnames'
import './BlockSelect.scss'

export enum BlockSelectType {
  small = 'type-small',
  extras = 'type-extras',
  acco = 'type-acco',
  row = 'type-row',
  region = 'type-region',
  button = 'type-button',
  card = 'type-card',
}

interface BlockSelectProps {
  model: string
  value?: string | number
  useControl?: boolean
  inputType?: 'checkbox' | 'radio'
  type?: BlockSelectType
  onChange?: (value: string, checked: boolean) => void
  className?: string
  headerText?: string | JSX.Element
  nrCols?: number[]
  slider?: boolean
  minContent?: boolean
  disabled?: boolean
  animated?: boolean
  children?: React.ReactNode | undefined
}

const BlockSelect: React.FC<BlockSelectProps> = props => {
  const blockSelectclasses = classNames(
    'srprs-blockselect',
    props.type ? props.type : false,
    props.animated ? 'is-animated' : false,
  )
  const renderChildrenApi = () => {
    return React.Children.map(props.children, child => {
      const childElement = child as React.ReactElement<any>
      if (childElement) {
        return React.cloneElement(childElement, {
          type: childElement.props.type || props.inputType,
          model: childElement.props.model || props.model,
          useControl: props.useControl,
          defaultValue: props.value,
          onChange: props.onChange,
        })
      }
    })
  }

  return (
    <>
      {props.headerText ? (
        <header
          className={`srprs-blockselect-header ${
            props.disabled ? 'disabled' : ''
          }`}
        >
          {props.headerText}
        </header>
      ) : null}
      <Grid
        className={blockSelectclasses}
        nrCols={props.nrCols ? props.nrCols : [1]}
        colGap={
          props.type === BlockSelectType.row ||
          props.type === BlockSelectType.region
            ? [GridGap.zero]
            : [GridGap.m]
        }
        slider={props.slider}
        minContent={props.minContent}
      >
        {renderChildrenApi()}
      </Grid>
    </>
  )
}

BlockSelect.defaultProps = {
  inputType: 'radio',
}

interface BlockItemProps {
  name?: string
  model?: string
  useControl?: boolean
  type?: 'checkbox' | 'radio'
  defaultValue?: string | number
  value?: string | number
  onChange?: (value: string, checked: boolean) => void
  disabled?: boolean
  included?: boolean
  icon?: JSX.Element
  illustration?: JSX.Element
  label?: JSX.Element | string
  labelSubline?: JSX.Element | string
  labelButton?: JSX.Element
  labelClassName?: string
  badge?: JSX.Element
  footerText?: JSX.Element | string
  footerTextAfter?: JSX.Element | string
  footerRating?: JSX.Element | string
  footerButton?: JSX.Element
  children?: React.ReactNode | undefined
}

const BlockItem: React.FC<BlockItemProps> = props => {
  // An absent prop must drop out of the id, not interpolate as "undefined":
  // every control missing one collapsed to the same id, and duplicates make
  // label[for] resolve to the first match. With nothing to build from, render
  // no id at all rather than an empty one — `id=""` is invalid and slips past
  // the duplicate check, which skips falsy ids.
  const id =
    [props.model || props.name, props.value]
      .filter(part => part !== undefined && part !== '')
      .join('-') || undefined
  const handleChange = (e: any) => {
    const target = e.currentTarget
    const checked = target.type === 'checkbox' ? target.checked : true

    if (props.onChange !== undefined) {
      props.onChange(target.value, checked)
    }
  }

  const Tag = props.useControl
    ? props.type === 'checkbox'
      ? Control.checkbox
      : Control.radio
    : 'input'
  let extraClasses = ``
  if (props.badge) {
    extraClasses += `label-badge ${
      props.labelClassName ? props.labelClassName : ''
    }`
  }

  const {
    footerText,
    footerButton,
    badge,
    illustration,
    footerTextAfter,
    defaultValue,
    type,
    model,
    name,
    value,
    disabled,
    included,
    label,
    labelSubline,
    labelButton,
    icon,
    footerRating,
  } = props

  return (
    <Col>
      <Tag
        type={type}
        id={id}
        name={model ? model : name}
        value={value}
        onChange={handleChange}
        model={model || ''}
        checked={defaultValue ? defaultValue === value : false}
        disabled={disabled || included}
      />
      <label htmlFor={id} className={extraClasses}>
        {icon ? ( // Type extras specific
          <span className="label-icon">
            <Icon icon={icon} />
          </span>
        ) : null}
        {label ? (
          <span className={`label-content`}>
            {label ? <span>{label}</span> : null}
            {labelSubline ? <small>{labelSubline}</small> : null}
            {labelButton ? labelButton : null}
          </span>
        ) : null}
        {badge}
        {illustration ? ( // Type acco specific
          <span className="label-illustration">{illustration}</span>
        ) : null}

        {!!footerText || !!footerRating || !!footerButton ? (
          <footer>
            <div className="footer-top">
              {/* Top left */}
              {footerText ? (
                <strong>{footerText}</strong>
              ) : footerRating ? (
                <span className={'rating'}>
                  <Icon icon={UI.star} />
                  &nbsp;{footerRating}
                </span>
              ) : (
                <span></span>
              )}

              {/* Top right */}
              {footerButton}
            </div>
            <div className="footer-bottom">
              <div className="footer-bottom">
                <span>{footerTextAfter}</span>
              </div>
            </div>
          </footer>
        ) : null}

        <Icon icon={UI.check} />
      </label>
    </Col>
  )
}

export { BlockSelect, BlockItem }
